import React, { useMemo, useState } from 'react';
import {
  Button,
  Callout,
  Field,
  FormGroup,
  Input,
  Textarea,
} from '../../ui/v2';
import { type EditorValues, toPatch, validate, valuesFrom } from './form';
import type { InquiryKindConfig } from './kind';
import type { EditableFields, InquiryRecord } from './model';

/**
 * Inline editor for the fields the API accepts. Saves only what changed;
 * nothing changed closes without a request.
 */
export const InquiryEditor: React.FC<{
  config: InquiryKindConfig;
  record: InquiryRecord;
  onSubmit: (patch: EditableFields) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
  error?: string | null;
}> = ({ config, record, onSubmit, onCancel, submitting, error }) => {
  const initial = useMemo(() => valuesFrom(record), [record]);
  const [v, setV] = useState<EditorValues>(initial);
  const [touched, setTouched] = useState<
    Partial<Record<keyof EditorValues, boolean>>
  >({});
  const [submitted, setSubmitted] = useState(false);
  const errors = useMemo(() => validate(v), [v]);
  const show = (k: keyof EditorValues) =>
    submitted || touched[k] ? errors[k] : undefined;
  const upd =
    (k: keyof EditorValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV((s) => ({ ...s, [k]: e.target.value }));
  const blur = (k: keyof EditorValues) => () =>
    setTouched((t) => ({ ...t, [k]: true }));
  const freeTextDate =
    !!v.preferred_date && !/^\d{4}-\d{2}-\d{2}$/.test(v.preferred_date);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length) return;
    const patch = toPatch(v, initial);
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    await onSubmit(patch);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      {error && <Callout tone="danger">{error}</Callout>}

      <FormGroup title="Guest" description="Who to get back to.">
        <Field
          label="Guest name"
          error={show('guest_name')}
          className="sm:col-span-2"
        >
          {(id, by) => (
            <Input
              id={id}
              aria-describedby={by}
              invalid={!!show('guest_name')}
              value={v.guest_name}
              onChange={upd('guest_name')}
              onBlur={blur('guest_name')}
              placeholder="Full name"
              autoComplete="off"
              autoFocus
            />
          )}
        </Field>
        <Field label="Phone" error={show('phone')}>
          {(id, by) => (
            <Input
              id={id}
              aria-describedby={by}
              type="tel"
              inputMode="tel"
              invalid={!!show('phone')}
              value={v.phone}
              onChange={upd('phone')}
              onBlur={blur('phone')}
              placeholder="(201) 555-0123"
            />
          )}
        </Field>
        <Field label="Email" error={show('email')} optional>
          {(id, by) => (
            <Input
              id={id}
              aria-describedby={by}
              type="email"
              inputMode="email"
              invalid={!!show('email')}
              value={v.email}
              onChange={upd('email')}
              onBlur={blur('email')}
              placeholder="name@example.com"
            />
          )}
        </Field>
      </FormGroup>

      <FormGroup
        title={config.recordLabel}
        description="What they’re planning."
        cols={4}
      >
        <Field
          label={config.labels.occasion}
          className="col-span-2 sm:col-span-4"
        >
          {(id) => (
            <Input id={id} value={v.occasion} onChange={upd('occasion')} />
          )}
        </Field>
        <Field
          label={config.labels.date}
          hint={
            freeTextDate
              ? `Arthur heard “${initial.preferred_date}”. Pick a date to confirm it.`
              : undefined
          }
          className="col-span-2"
        >
          {(id, by) =>
            freeTextDate ? (
              <div className="flex gap-2">
                <Input
                  id={id}
                  aria-describedby={by}
                  value={v.preferred_date}
                  onChange={upd('preferred_date')}
                  className="flex-1"
                />
                <Input
                  type="date"
                  aria-label="Pick a date"
                  className="w-[150px] shrink-0"
                  onChange={(e) =>
                    e.target.value &&
                    setV((s) => ({ ...s, preferred_date: e.target.value }))
                  }
                />
              </div>
            ) : (
              <Input
                id={id}
                aria-describedby={by}
                type="date"
                value={v.preferred_date}
                onChange={upd('preferred_date')}
              />
            )
          }
        </Field>
        <Field label={config.labels.time} optional>
          {(id) => (
            <Input
              id={id}
              value={v.preferred_time}
              onChange={upd('preferred_time')}
              placeholder="7:00 PM"
            />
          )}
        </Field>
        <Field label={config.labels.guestCount}>
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={v.guest_count}
              onChange={upd('guest_count')}
              placeholder="12"
              className="v2-num"
            />
          )}
        </Field>
        <Field
          label="Special requests"
          optional
          className="col-span-2 sm:col-span-4"
        >
          {(id) => (
            <Textarea
              id={id}
              value={v.special_requests}
              onChange={upd('special_requests')}
              rows={3}
            />
          )}
        </Field>
      </FormGroup>

      <div className="flex items-center justify-end gap-2 border-t border-v2-line pt-4">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" loading={submitting}>
          Save changes
        </Button>
      </div>
    </form>
  );
};
